import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import './GoldDifferenceTimeline.css';
import { DetailedKill, DetailedPlate, DetailedTurret, DetailedMisc, CSPMBreakpoint, CSPMRange } from './EnhancedGoldControls';
import GameAssetsContext from '../../../../XXX/context/GameAssetContext';

type GoldEventType = 'kill' | 'turret' | 'plate' | 'cspm' | 'misc' | 'quest';

interface GoldEvent {
  type: GoldEventType;
  wave: number;
  timing: string;
  goldValue: number;
  description: string;
  champion?: 'primary' | 'opponent';
  cspm?: number; // For CSPM events
}

interface StageData {
  key: 'early1' | 'early2' | 'mid' | 'late';
  label: string;
  waveRange: [number, number];
  timeRange: [number, number]; // in minutes
  primaryGold: number;
  opponentGold: number;
}

interface Props {
  primaryChampionName: string;
  opponentChampionName?: string;
  kills: DetailedKill[];
  plates: DetailedPlate[];
  turrets?: DetailedTurret[];
  miscellaneous?: DetailedMisc[];
  cspmBreakpoints?: CSPMBreakpoint[];
  cspmRanges?: CSPMRange[];
  opponentKills?: DetailedKill[];
  opponentPlates?: DetailedPlate[];
  opponentTurrets?: DetailedTurret[];
  opponentMiscellaneous?: DetailedMisc[];
  opponentCspmBreakpoints?: CSPMBreakpoint[];
  opponentCspmRanges?: CSPMRange[];
  primaryGoldInputs?: any;
  opponentGoldInputs?: any;
  primaryCalculatedWaves?: any[];
  opponentCalculatedWaves?: any[];
  primarySummaries?: any;
  opponentSummaries?: any;
  primaryWaveProgression?: any[]; // WaveBuildProgression[]
  opponentWaveProgression?: any[]; // WaveBuildProgression[]
  primaryQuestCompletionWave?: number | null;
  opponentQuestCompletionWave?: number | null;
  primaryQuestImmediateGold?: number;
  opponentQuestImmediateGold?: number;
  cspm?: number;
  className?: string;
}

const MAX_TIME = 45;              // minutes shown on the full timeline
const WINDOW_MIN = 20;            // visible window (minutes) in scroll mode
const PAD_Y = 20;                 // vertical inner padding (px)
const Y_PAD_PCT = 0.05;           // 5% breathing room on Y scale
const BASE_HEIGHT = 300;          // default height (px) - now same as expanded
const EXPANDED_HEIGHT = 300;      // expanded height (px)
const Y_AXIS_WIDTH = 60;          // sticky Y axis column width (px)

// helpers
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

const GoldDifferenceTimeline: React.FC<Props> = ({
  primaryChampionName,
  opponentChampionName = 'Opponent',
  kills,
  plates,
  turrets = [],
  miscellaneous = [],
  cspmBreakpoints = [],
  cspmRanges = [],
  opponentKills = [],
  opponentPlates = [],
  opponentTurrets = [],
  opponentMiscellaneous = [],
  opponentCspmBreakpoints = [],
  opponentCspmRanges = [],
  primaryGoldInputs,
  opponentGoldInputs,
  primaryCalculatedWaves = [],
  opponentCalculatedWaves = [],
  primarySummaries,
  opponentSummaries,
  primaryWaveProgression = [],
  opponentWaveProgression = [],
  primaryQuestCompletionWave,
  opponentQuestCompletionWave,
  primaryQuestImmediateGold = 0,
  opponentQuestImmediateGold = 0,
  cspm = 10,
  className = ''
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const [expanded, setExpanded] = useState(false);

  // Scroll container ref (the element that horizontally scrolls in default mode)
  const scrollRef = useRef<HTMLDivElement>(null);

  // Live measurements
  const [viewportW, setViewportW] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const rafRef = useRef<number | null>(null);

  // --- Measurement: keep viewportW in sync with the scroll container's clientWidth
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const measure = () => setViewportW(el.clientWidth);

    measure();

    // ResizeObserver for accurate parent/viewport changes
    const RO = (window as any).ResizeObserver as typeof ResizeObserver | undefined;
    const ro = RO ? new RO(() => measure()) : undefined;
    ro?.observe(el);

    // Fallback
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, []);

  // --- Scroll handling (throttled via rAF)
  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (expanded) return;
    const el = e.currentTarget;
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        setScrollLeft(el.scrollLeft);
        rafRef.current = null;
      });
    }
  }, [expanded]);

  // --- Expand toggle
  const toggleExpanded = useCallback(() => {
    setExpanded(prev => {
      const next = !prev;
      if (!prev) {
        // entering expanded: reset scroll to left
        const el = scrollRef.current;
        if (el) el.scrollTo({ left: 0 });
        setScrollLeft(0);
      }
      return next;
    });
  }, []);

  // --- Time utilities
  const timingToMinutes = (timing: string): number => {
    const [m, s] = timing.split(':').map(Number);
    return m + (s || 0) / 60;
  };

  const waveToMinutes = (wave: number): number => {
    const arrivalSeconds = 65 + (wave - 1) * 30 + 25;
    const clearSeconds = arrivalSeconds + 30;
    return clearSeconds / 60;
  };

  // --- Events (combine both champion's events)
  const allEvents: GoldEvent[] = useMemo(() => {
    const waveToTiming = (wave: number): string => {
      const arrivalSeconds = 65 + (wave - 1) * 30 + 25;
      const clearSeconds = arrivalSeconds + 30;
      const minutes = Math.floor(clearSeconds / 60);
      const seconds = clearSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const list: GoldEvent[] = [
      // Primary champion events
      ...kills.map(k => ({
        type: 'kill' as const,
        wave: k.wave,
        timing: k.timing,
        goldValue: k.value,
        description: `${primaryChampionName} Kill (${k.value}g)`,
        champion: 'primary' as const
      })),
      ...plates.map(p => ({
        type: 'plate' as const,
        wave: p.wave,
        timing: p.timing,
        goldValue: p.goldValue,
        description: `${primaryChampionName} ${p.count} Plates ${p.position} (${p.goldValue}g)`,
        champion: 'primary' as const
      })),
      ...turrets.map(t => ({
        type: 'turret' as const,
        wave: t.wave,
        timing: t.timing,
        goldValue: t.localGold + t.globalGold,
        description: `${primaryChampionName} ${t.type} Turret ${t.position} (${t.localGold + t.globalGold}g)`,
        champion: 'primary' as const
      })),
      ...miscellaneous.map(m => ({
        type: 'misc' as const,
        wave: m.wave,
        timing: m.timing,
        goldValue: m.value,
        description: `${primaryChampionName} Misc Gold (${m.value}g)`,
        champion: 'primary' as const
      })),
      ...cspmBreakpoints.map(bp => ({
        type: 'cspm' as const,
        wave: bp.wave,
        timing: bp.timing,
        goldValue: 0,
        cspm: bp.cspm,
        description: `${primaryChampionName} CSPM → ${bp.cspm}`,
        champion: 'primary' as const
      })),
      ...cspmRanges.map(r => ({
        type: 'cspm' as const,
        wave: r.fromWave,
        timing: waveToTiming(r.fromWave),
        goldValue: 0,
        cspm: r.cspm,
        description: `${primaryChampionName} CSPM → ${r.cspm}`,
        champion: 'primary' as const
      })),
      // Opponent champion events
      ...opponentKills.map(k => ({
        type: 'kill' as const,
        wave: k.wave,
        timing: k.timing,
        goldValue: k.value,
        description: `${opponentChampionName} Kill (${k.value}g)`,
        champion: 'opponent' as const
      })),
      ...opponentPlates.map(p => ({
        type: 'plate' as const,
        wave: p.wave,
        timing: p.timing,
        goldValue: p.goldValue,
        description: `${opponentChampionName} ${p.count} Plates ${p.position} (${p.goldValue}g)`,
        champion: 'opponent' as const
      })),
      ...opponentTurrets.map(t => ({
        type: 'turret' as const,
        wave: t.wave,
        timing: t.timing,
        goldValue: t.localGold + t.globalGold,
        description: `${opponentChampionName} ${t.type} Turret ${t.position} (${t.localGold + t.globalGold}g)`,
        champion: 'opponent' as const
      })),
      ...opponentMiscellaneous.map(m => ({
        type: 'misc' as const,
        wave: m.wave,
        timing: m.timing,
        goldValue: m.value,
        description: `${opponentChampionName} Misc Gold (${m.value}g)`,
        champion: 'opponent' as const
      })),
      ...opponentCspmBreakpoints.map(bp => ({
        type: 'cspm' as const,
        wave: bp.wave,
        timing: bp.timing,
        goldValue: 0,
        cspm: bp.cspm,
        description: `${opponentChampionName} CSPM → ${bp.cspm}`,
        champion: 'opponent' as const
      })),
      ...opponentCspmRanges.map(r => ({
        type: 'cspm' as const,
        wave: r.fromWave,
        timing: waveToTiming(r.fromWave),
        goldValue: 0,
        cspm: r.cspm,
        description: `${opponentChampionName} CSPM → ${r.cspm}`,
        champion: 'opponent' as const
      })),
      // Quest completion events
      ...(primaryQuestCompletionWave ? [{
        type: 'quest' as const,
        wave: primaryQuestCompletionWave,
        timing: waveToTiming(primaryQuestCompletionWave),
        goldValue: primaryQuestImmediateGold,
        description: `${primaryChampionName} Role Quest Complete${primaryQuestImmediateGold ? ` (+${primaryQuestImmediateGold}g)` : ''}`,
        champion: 'primary' as const
      }] : []),
      ...(opponentQuestCompletionWave ? [{
        type: 'quest' as const,
        wave: opponentQuestCompletionWave,
        timing: waveToTiming(opponentQuestCompletionWave),
        goldValue: opponentQuestImmediateGold,
        description: `${opponentChampionName} Role Quest Complete${opponentQuestImmediateGold ? ` (+${opponentQuestImmediateGold}g)` : ''}`,
        champion: 'opponent' as const
      }] : [])
    ];
    return list.sort((a, b) => a.wave - b.wave);
  }, [kills, plates, turrets, miscellaneous, cspmBreakpoints, cspmRanges, opponentKills, opponentPlates, opponentTurrets, opponentMiscellaneous, opponentCspmBreakpoints, opponentCspmRanges, primaryChampionName, opponentChampionName, primaryQuestCompletionWave, opponentQuestCompletionWave, primaryQuestImmediateGold, opponentQuestImmediateGold]);

  // --- Stages
  const stages: StageData[] = useMemo(() => ([
    {
      key: 'early1',
      label: 'Early',
      waveRange: [1, 10],
      timeRange: [0, 8.5],
      primaryGold: primarySummaries?.early1?.goldAtBoundary ?? 0,
      opponentGold: opponentSummaries?.early1?.goldAtBoundary ?? 0
    },
    {
      key: 'early2',
      label: 'Early-Mid',
      waveRange: [11, 26],
      timeRange: [8.5, 14.5],
      primaryGold: primarySummaries?.early2?.goldAtBoundary ?? 0,
      opponentGold: opponentSummaries?.early2?.goldAtBoundary ?? 0
    },
    {
      key: 'mid',
      label: 'Mid',
      waveRange: [27, 47],
      timeRange: [14.5, 25],
      primaryGold: primarySummaries?.mid?.goldAtBoundary ?? 0,
      opponentGold: opponentSummaries?.mid?.goldAtBoundary ?? 0
    },
    {
      key: 'late',
      label: 'Late',
      waveRange: [48, 80],
      timeRange: [25, MAX_TIME],
      primaryGold: primarySummaries?.late?.goldAtBoundary ?? 0,
      opponentGold: opponentSummaries?.late?.goldAtBoundary ?? 0
    }
  ]), [primarySummaries, opponentSummaries]);

  // --- Gold progression (build once per data change)
  const goldProgression = useMemo(() => {
    const pts: Array<{ time: number; primaryGold: number; opponentGold: number; hasEvent?: boolean; events?: GoldEvent[] }> = [];

    // Start point
    pts.push({ time: 0, primaryGold: 500, opponentGold: 500 });

    // Use calculated waves if available (preferred method)
    if (primaryCalculatedWaves.length > 0 || opponentCalculatedWaves.length > 0) {
      const maxWaves = Math.max(primaryCalculatedWaves.length, opponentCalculatedWaves.length, 80);
      for (let i = 0; i < maxWaves; i++) {
        const pw = primaryCalculatedWaves[i];
        const ow = opponentCalculatedWaves[i];
        if (!pw && !ow) break;

        const waveTime = pw ? pw.gameTime : (ow ? ow.gameTime : waveToMinutes(i + 1));
        const primaryGold = pw ? (pw.totalGold + 500) : 500; // Add starting gold
        const opponentGold = ow ? (ow.totalGold + 500) : 500; // Add starting gold

        const waveNumber = i + 1;
        const waveEvents = allEvents.filter(e => e.wave === waveNumber);

        pts.push({
          time: waveTime,
          primaryGold,
          opponentGold,
          hasEvent: waveEvents.length > 0,
          events: waveEvents.length ? waveEvents : undefined
        });
      }

      // Ensure we hit the full timeline end
      if (pts[pts.length - 1].time < MAX_TIME) {
        const last = pts[pts.length - 1];
        pts.push({ time: MAX_TIME, primaryGold: last.primaryGold, opponentGold: last.opponentGold });
      }
    }
    // Fallback to old wave progression data
    else if (primaryWaveProgression.length > 0 || opponentWaveProgression.length > 0) {
      const maxWaves = Math.max(primaryWaveProgression.length, opponentWaveProgression.length, 30);
      for (let i = 0; i < maxWaves; i++) {
        const pw = primaryWaveProgression[i];
        const ow = opponentWaveProgression[i];
        if (!pw && !ow) break;

        const waveTime = pw
          ? timingToMinutes(pw.clearTiming)
          : ow
            ? timingToMinutes(ow.clearTiming)
            : waveToMinutes(i + 1);

        const primaryGold = pw ? pw.totalGold : 500;
        const opponentGold = ow ? ow.totalGold : 500;

        const waveNumber = i + 1;
        const waveEvents = allEvents.filter(e => e.wave === waveNumber);

        pts.push({
          time: waveTime,
          primaryGold,
          opponentGold,
          hasEvent: waveEvents.length > 0,
          events: waveEvents.length ? waveEvents : undefined
        });
      }

      // Ensure we hit the full timeline end
      if (pts[pts.length - 1].time < MAX_TIME) {
        const last = pts[pts.length - 1];
        pts.push({ time: MAX_TIME, primaryGold: last.primaryGold, opponentGold: last.opponentGold });
      }
    } else {
      // Fallback: rough CSPM + events model
      let p = 500;
      let o = 500;
      const ordered = [...allEvents].sort((a, b) => timingToMinutes(a.timing) - timingToMinutes(b.timing));

      for (const ev of ordered) {
        const t = timingToMinutes(ev.timing);
        const prevT = pts[pts.length - 1]?.time ?? 0;
        const dt = Math.max(0, t - prevT);
        const base = Math.max(0, (dt - 1.5)) * cspm * 20 + dt * 2.04;

        p += base + ev.goldValue;
        o += base;

        pts.push({ time: t, primaryGold: p, opponentGold: o, hasEvent: true, events: [ev] });
      }

      if (pts[pts.length - 1].time < MAX_TIME) {
        const prevT = pts[pts.length - 1]?.time ?? 0;
        const dt = MAX_TIME - prevT;
        const base = Math.max(0, (dt - 1.5)) * cspm * 20 + dt * 2.04;
        p += base; o += base;
        pts.push({ time: MAX_TIME, primaryGold: p, opponentGold: o });
      }
    }

    // Ensure strictly increasing times
    return pts
      .sort((a, b) => a.time - b.time)
      .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time);
  }, [primaryCalculatedWaves, opponentCalculatedWaves, primaryWaveProgression, opponentWaveProgression, allEvents, cspm]);

  // --- Layout & scaling ---

  // visible window size in minutes
  const windowMinutes = expanded ? MAX_TIME : WINDOW_MIN;

  // px per minute so that the visible window exactly matches the viewport width
  const pxPerMinute = Math.max(1, viewportW / Math.max(1, windowMinutes));

  // full content width for 0..MAX_TIME
  const svgFullW = Math.ceil(pxPerMinute * MAX_TIME);

  // clamp scrollLeft to actual content bounds
  const maxScrollLeft = Math.max(0, svgFullW - viewportW);
  const clampedScrollLeft = clamp(scrollLeft, 0, maxScrollLeft);

  // when the viewport width changes, ensure DOM scrollLeft is valid
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const capped = clamp(el.scrollLeft, 0, Math.max(0, svgFullW - el.clientWidth));
    if (capped !== el.scrollLeft) {
      el.scrollLeft = capped;
      setScrollLeft(capped);
    }
  }, [svgFullW, viewportW]);

  // the actual visible time window from clamped scroll
  const visibleStartTime = expanded ? 0 : clampedScrollLeft / pxPerMinute;
  const visibleEndTime = expanded ? MAX_TIME : Math.min(MAX_TIME, visibleStartTime + windowMinutes);

  // visible points for drawing & Y scaling
  const visiblePoints = useMemo(
    () => goldProgression.filter(p => p.time >= visibleStartTime && p.time <= visibleEndTime),
    [goldProgression, visibleStartTime, visibleEndTime]
  );

  // Y scale derived from visible slice (with a little padding)
  const yVals = visiblePoints.length
    ? visiblePoints.flatMap(p => [p.primaryGold, p.opponentGold])
    : [0, 1000];

  const rawMin = Math.min(...yVals);
  const rawMax = Math.max(...yVals);
  const span = Math.max(1, rawMax - rawMin);
  const yMin = Math.max(0, Math.floor((rawMin - span * Y_PAD_PCT) / 100) * 100);
  const yMax = Math.ceil((rawMax + span * Y_PAD_PCT) / 100) * 100;
  const yRange = Math.max(1, yMax - yMin);

  const timelineH = expanded ? EXPANDED_HEIGHT : BASE_HEIGHT;

  // mappers
  const timeToX = (t: number) => t * pxPerMinute;
  const goldToY = (g: number) =>
    PAD_Y + (timelineH - 2 * PAD_Y) - ((g - yMin) / yRange) * (timelineH - 2 * PAD_Y);

  // axis ticks (every 5 minutes)
  const ticks = useMemo(() => (
    Array.from({ length: Math.floor(MAX_TIME / 5) + 1 }, (_, i) => i * 5)
  ), []);

  // stage boundaries
  const stageBoundaries = stages.slice(0, -1).map(s => s.timeRange[1]);

  return (
    <div className={`gold-difference-timeline ${className}`}>
      <div className="gdt-header">
        <h6>Champion Gold Progression</h6>
        <div className="gdt-header-right">
          {/* Simplified Legend - Next to Arrow Button */}
          <div className="gdt-legend-header">
            <div className="gdt-legend-item">
              <div className="gdt-legend-line gdt-legend-primary" />
              <span>{primaryChampionName} Gold</span>
            </div>
            <div className="gdt-legend-item">
              <div className="gdt-legend-line gdt-legend-opponent" />
              <span>{opponentChampionName} Gold</span>
            </div>
          </div>
          <button
            className="gdt-expand-btn"
            onClick={toggleExpanded}
            title={expanded ? 'Collapse to scrolling 20m view' : 'Expand to full 0–45m view'}
          >
            {expanded ? (
              <FontAwesomeIcon icon={faCompress} />
            ) : (
              <FontAwesomeIcon icon={faExpand} />
            )}
          </button>
        </div>
      </div>

      <div className="gdt-chart-row">
        {/* Sticky Y-axis column */}
        <div className="gdt-y-axis gdt-y-axis-sticky" style={{ height: timelineH }}>
          <div className="gdt-y-label" style={{ top: PAD_Y }}>{yMax}g</div>
          <div className="gdt-y-label" style={{ top: timelineH / 2 }}>{Math.round((yMax + yMin) / 2)}g</div>
          <div className="gdt-y-label" style={{ top: timelineH - PAD_Y }}>{yMin}g</div>
        </div>

        {/* Scroll container (contains the wide content) */}
        <div
          ref={scrollRef}
          className={`gdt-chart-container ${expanded ? 'is-expanded' : 'is-scrollable'}`}
          onScroll={onScroll}
          style={{ height: timelineH }}
        >
          <div className="gdt-content" style={{ width: svgFullW, minWidth: svgFullW, height: timelineH }}>
            <svg className="gdt-svg" width={svgFullW} height={timelineH} role="img" aria-label="Gold timeline">
              {/* Background grid & frame */}
              <defs>
                <pattern id="gdt-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--border-secondary)" strokeWidth="1" opacity="0.25" />
                </pattern>
              </defs>
              <rect x={0} y={PAD_Y} width={svgFullW} height={timelineH - 2 * PAD_Y} fill="url(#gdt-grid)" />
              <rect x={0} y={PAD_Y} width={svgFullW} height={timelineH - 2 * PAD_Y} fill="none" stroke="var(--border-primary)" strokeWidth="1" />

              {/* Stage boundaries & labels */}
              {stageBoundaries.map((t, i) => (
                <line
                  key={`stage-bound-${i}`}
                  x1={timeToX(t)}
                  x2={timeToX(t)}
                  y1={PAD_Y}
                  y2={timelineH - PAD_Y}
                  stroke="var(--text-accent)"
                  strokeWidth={2}
                  strokeDasharray="5,5"
                  opacity={0.7}
                />
              ))}

              {stages.map((s, i) => {
                const xCenter = (timeToX(s.timeRange[0]) + timeToX(s.timeRange[1])) / 2;
                return (
                  <text
                    key={`stage-label-${i}`}
                    x={xCenter}
                    y={PAD_Y - 6}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight={600}
                    fill="var(--text-accent)"
                  >
                    {s.label}
                  </text>
                );
              })}

              {/* Lines: draw only the visible slice for crisp edges */}
              {visiblePoints.length > 1 && (
                <polyline
                  points={visiblePoints.map(p => `${timeToX(p.time)},${goldToY(p.primaryGold)}`).join(' ')}
                  fill="none"
                  stroke="var(--event-blue-cspm)"
                  strokeWidth={3}
                />
              )}

              {visiblePoints.length > 1 && (
                <polyline
                  points={visiblePoints.map(p => `${timeToX(p.time)},${goldToY(p.opponentGold)}`).join(' ')}
                  fill="none"
                  stroke="var(--event-red-cspm)"
                  strokeWidth={3}
                />
              )}

              {/* Event icons (only those in window, positioned on correct champion's line) */}
              {visiblePoints.filter(p => p.hasEvent).flatMap((p, idx) => {
                // Group events by champion for this time point
                const primaryEvents = (p.events || []).filter(e => e.champion === 'primary');
                const opponentEvents = (p.events || []).filter(e => e.champion === 'opponent');
                
                const renderEventStack = (events: GoldEvent[], baseY: number, champion: 'primary' | 'opponent') => {
                  return events.map((event, eventIdx) => {
                    const getEventIcon = () => {
                      switch (event.type) {
                        case 'kill':
                          return gameWideAssets?.damageIconWhite;
                        case 'turret':
                          return gameWideAssets?.turretIcon;
                        case 'plate':
                          return gameWideAssets?.turretPlateIcon;
                        case 'cspm':
                          return gameWideAssets?.minionIcon;
                        default:
                          return null;
                      }
                    };

                    const getEventColor = () => {
                      // Use the same dark colors as main timeline - all events use cspm color
                      return champion === 'primary' ? 'var(--event-blue-cspm)' : 'var(--event-red-cspm)';
                    };

                    const iconUrl = getEventIcon();
                    // Stack events vertically: each event gets offset by 25px from the base position
                    // Primary champion events stack upward (negative offset)
                    // Opponent champion events stack downward (positive offset)
                    const stackOffset = champion === 'primary' ? -25 * eventIdx : 25 * eventIdx;
                    const yPosition = baseY + stackOffset;
                    
                    return (
                      <g key={`evt-${idx}-${champion}-${eventIdx}`}>
                        {event.type === 'quest' ? (
                          <>
                            {/* Quest completion: golden circle with R */}
                            <circle
                              cx={timeToX(p.time)}
                              cy={yPosition}
                              r={11}
                              fill="#c89b3c"
                              stroke="#f0e6d2"
                              strokeWidth={1.5}
                              className="gdt-event-bg"
                            />
                            <text
                              x={timeToX(p.time)}
                              y={yPosition}
                              textAnchor="middle"
                              dominantBaseline="central"
                              fontSize="12"
                              fontWeight="bold"
                              fill="white"
                            >
                              R
                            </text>
                          </>
                        ) : iconUrl ? (
                          <>
                            {/* Colored background circle */}
                            <circle
                              cx={timeToX(p.time)}
                              cy={yPosition}
                              r={10}
                              fill={getEventColor()}
                              stroke="white"
                              strokeWidth={1}
                              className="gdt-event-bg"
                            />
                            {/* Icon */}
                            <image
                              href={iconUrl}
                              x={timeToX(p.time) - 8}
                              y={yPosition - 8}
                              width={16}
                              height={16}
                              className="gdt-event-icon"
                            />
                          </>
                        ) : event.type === 'misc' ? (
                          // Misc events with question mark
                          <>
                            <circle
                              cx={timeToX(p.time)}
                              cy={yPosition}
                              r={10}
                              fill={getEventColor()}
                              stroke="white"
                              strokeWidth={1}
                              className="gdt-event-bg"
                            />
                            <text
                              x={timeToX(p.time)}
                              y={yPosition}
                              textAnchor="middle"
                              dominantBaseline="central"
                              fontSize="12"
                              fontWeight="bold"
                              fill="white"
                              className="gdt-misc-text"
                            >
                              ?
                            </text>
                          </>
                        ) : (
                          // Other fallback events
                          <circle
                            cx={timeToX(p.time)}
                            cy={yPosition}
                            r={6}
                            fill={getEventColor()}
                            stroke="white"
                            strokeWidth={2}
                            className="gdt-event-dot"
                          />
                        )}
                        <title>{event.description}</title>
                      </g>
                    );
                  });
                };

                return [
                  ...renderEventStack(primaryEvents, goldToY(p.primaryGold), 'primary'),
                  ...renderEventStack(opponentEvents, goldToY(p.opponentGold), 'opponent')
                ];
              })}
            </svg>

            {/* X-axis ticks (scroll with content) */}
            <div className="gdt-x-axis" style={{ width: svgFullW }}>
              {ticks.map(t => (
                <div key={t} className="gdt-x-label" style={{ left: timeToX(t) }}>
                  {t}m
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stages summary */}
  {/*     <div className="gdt-stages-simplified">
        {stages.map(s => {
          if ((s.primaryGold ?? 0) <= 0 && (s.opponentGold ?? 0) <= 0) return null;
          const diff = (s.primaryGold ?? 0) - (s.opponentGold ?? 0);
          return (
            <div key={s.key} className="gdt-stage-simple">
              <div className="gdt-stage-name">{s.label}</div>
              <div className="gdt-stage-time">({s.timeRange[0]}–{s.timeRange[1]}m)</div>
              <div className="gdt-stage-values">
                <span className="gdt-primary-gold">{s.primaryGold}g</span>
                <span className="gdt-opponent-gold">{s.opponentGold}g</span>
                <span className={`gdt-difference-gold ${diff >= 0 ? 'positive' : 'negative'}`}>
                  {diff >= 0 ? '+' : ''}{diff}g
                </span>
              </div>
            </div>
          );
        })}
      </div> */}


    </div>
  );
};

export default GoldDifferenceTimeline;
